import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { entrevistasApi } from '../api/procesos'
import { colors, text, space, sx } from '../design/tokens'
import Boton from '../components/Boton'
import { BadgeEntrevista, linkEntrevista } from './ProcesosEntrevistasPage'

// Detalle de una entrevista de relevamiento: tareas estructuradas + transcripción.

const FRECUENCIA_LABEL = { diaria: 'Diaria', semanal: 'Semanal', mensual: 'Mensual', eventual: 'Eventual' }
const DECISION_LABEL = { regla: 'Regla', criterio: 'Criterio', mixta: 'Mixta' }

export default function ProcesosEntrevistaDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [ent, setEnt] = useState(null)
  const [copiado, setCopiado] = useState(false)

  const cargar = useCallback(() => {
    entrevistasApi.detalle(id).then(({ data }) => setEnt(data))
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  if (!ent) return <p style={{ color: colors.textSec }}>Cargando…</p>

  const copiarLink = () => {
    navigator.clipboard.writeText(linkEntrevista(ent.token))
    setCopiado(true)
    setTimeout(() => setCopiado(false), 1500)
  }

  const reabrir = async () => {
    await entrevistasApi.reabrir(ent.id)
    cargar()
  }

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar la entrevista de ${ent.nombre}? Se borran también las tareas relevadas.`)) return
    await entrevistasApi.eliminar(ent.id)
    navigate(-1)
  }

  return (
    <div>
      <div style={sx.toolbar}>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
        <div>
          <h1 style={{ margin: 0, fontSize: 20 }}>{ent.nombre}</h1>
          {(ent.area || ent.puesto) && (
            <div style={{ fontSize: text.sec, color: colors.textSec }}>
              {[ent.area, ent.puesto].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <BadgeEntrevista estado={ent.estado} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: space.md }}>
          <Boton variante="send" onClick={copiarLink}>{copiado ? '✓ Copiado' : '🔗 Copiar link'}</Boton>
          {ent.estado === 'completada' && (
            <Boton variante="secondary" onClick={reabrir}>🔓 Reabrir</Boton>
          )}
          <Boton variante="destructive" onClick={eliminar}>Eliminar</Boton>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: space.xl, alignItems: 'start' }}>
        {/* Tareas relevadas */}
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: `${space.lg}px ${space.xl}px`, borderBottom: `1px solid ${colors.border}` }}>
            <h2 style={sx.cardTitle}>Tareas relevadas ({ent.tareas?.length || 0})</h2>
          </div>
          {(ent.tareas?.length || 0) === 0 ? (
            <p style={{ padding: space.xl, color: colors.textSec, fontSize: text.body }}>
              Todavía no hay tareas guardadas — aparecen a medida que la IA las va estructurando durante la charla.
            </p>
          ) : (
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>Tarea</th>
                  <th style={sx.th}>Frecuencia</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Min</th>
                  <th style={sx.th}>Sistemas</th>
                  <th style={sx.th}>Decisión</th>
                </tr>
              </thead>
              <tbody>
                {ent.tareas.map(t => (
                  <tr key={t.id} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                    <td style={{ ...sx.td, padding: `${space.md}px ${space.xl}px`, maxWidth: 340 }}>
                      <div>{t.descripcion}</div>
                      {t.fricciones && (
                        <div style={{ fontSize: text.sec, color: '#92400e', marginTop: 2 }}>⚠ {t.fricciones}</div>
                      )}
                      {(t.entradas || t.salidas) && (
                        <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: 2 }}>
                          {t.entradas && <>← {t.entradas} </>}
                          {t.salidas && <>→ {t.salidas}</>}
                        </div>
                      )}
                    </td>
                    <td style={sx.td}>{FRECUENCIA_LABEL[t.frecuencia] || t.frecuencia || '—'}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{t.duracion_minutos ?? '—'}</td>
                    <td style={{ ...sx.td, fontSize: text.sec, color: colors.textSec }}>
                      {(t.sistemas || []).join(', ') || '—'}
                    </td>
                    <td style={sx.td}>
                      {t.tipo_decision ? (
                        <span style={{
                          ...sx.badge,
                          background: t.tipo_decision === 'regla' ? colors.okBg : t.tipo_decision === 'criterio' ? colors.warnBg : colors.bgSoft,
                          color: t.tipo_decision === 'regla' ? colors.ok : t.tipo_decision === 'criterio' ? colors.warn : colors.textSec,
                        }}>
                          {DECISION_LABEL[t.tipo_decision]}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Transcripción */}
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: `${space.lg}px ${space.xl}px`, borderBottom: `1px solid ${colors.border}` }}>
            <h2 style={sx.cardTitle}>Conversación ({ent.mensajes?.length || 0} mensajes)</h2>
          </div>
          <div style={{ padding: space.xl, display: 'flex', flexDirection: 'column', gap: space.md, maxHeight: 560, overflowY: 'auto' }}>
            {(ent.mensajes?.length || 0) === 0 ? (
              <p style={{ color: colors.textSec, fontSize: text.body, margin: 0 }}>
                El empleado todavía no abrió el link.
              </p>
            ) : ent.mensajes.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%',
                  padding: `${space.md}px ${space.lg}px`,
                  borderRadius: 10,
                  fontSize: text.sec,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  background: m.role === 'user' ? colors.linkBg : colors.bgSoft,
                  color: m.role === 'user' ? colors.link : colors.text,
                  border: `1px solid ${m.role === 'user' ? '#bfdbfe' : colors.border}`,
                }}
              >
                {m.texto}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
